import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Col, Row, Tag, Select, Tooltip, Button, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { FilterOutlined, CopyOutlined, CheckOutlined } from '@ant-design/icons';
import type { ColumnsType, TableProps } from 'antd/es/table';

import AppTable from '../../../../components/AppTable/AppTable';
import { DijikolayCustomer } from '../../../../redux/reducers/dijikolay/dijikolay.types';
import { RootState } from '../../../../redux/store';

interface DijikolayTableProps {
    onUpdateStatus: (id: number, status: string) => void;
    onUpdateContacted: (id: number) => void;
    onChange: TableProps<DijikolayCustomer>['onChange'];
}

const DijikolayTable: React.FC<DijikolayTableProps> = ({ onUpdateStatus, onUpdateContacted, onChange }) => {
    const { t } = useTranslation('dijikolay');
    const { customers, filters } = useSelector((state: RootState) => state.dijikolay);
    const [copiedEmailId, setCopiedEmailId] = useState<number | null>(null);

    const filteredCustomers = useMemo(() => {
        return customers.data.filter(customer => {
            const statusFilters = filters.status as string[] | null;
            const contactedFilter = filters.contacted as boolean | null;
            const statusMatch = !statusFilters || statusFilters.length === 0 || statusFilters.includes(customer.status);
            const contactedMatch = contactedFilter === null || contactedFilter === undefined || customer.contacted === contactedFilter;
            return statusMatch && contactedMatch;
        });
    }, [customers.data, filters]);

    const statusOptions = [
        { label: t('status_pending'), value: 'Bekliyor' },
        { label: t('status_in_progress'), value: 'İşleme Alındı' },
        { label: t('status_denied'), value: 'Red Edildi' },
        { label: t('status_cancelled'), value: 'İptal' },
        { label: t('status_completed'), value: 'Tamamlandı' },
    ];

    const contactedOptions = [
        { label: t('contacted_yes'), value: 'true' },
        { label: t('contacted_no'), value: 'false' },
    ];

    const handleCopy = (textToCopy: string, id: number) => {
        navigator.clipboard.writeText(textToCopy).then(() => {
            setCopiedEmailId(id);
            setTimeout(() => setCopiedEmailId(null), 2000);
        });
    };

    const columns: ColumnsType<DijikolayCustomer> = [
        {
            title: t('product'),
            dataIndex: 'product',
            key: 'product',
            render: (product: string | null) => {
                if (product && product.length > 18) {
                    return (
                        <Tooltip title={product}>
                            {`${product.substring(0, 18)}...`}
                        </Tooltip>
                    );
                }
                return product || '-';
            },
        },
        {
            title: t('customer'),
            dataIndex: 'customer',
            key: 'customer',
            render: (customer: string | null) => {
                if (customer && customer.length > 18) {
                    return (
                        <Tooltip title={customer}>
                            {`${customer.substring(0, 18)}...`}
                        </Tooltip>
                    );
                }
                return customer || '-';
            },
        },
        {
            title: t('phone'),
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: t('email'),
            dataIndex: 'email',
            key: 'email',
            render: (email: string | null, record: DijikolayCustomer) => {
                if (!email) return '-';
                const isLong = email.length > 18;
                const emailContent = isLong ? `${email.substring(0, 18)}...` : email;
                return (
                    <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Tooltip title={isLong ? email : undefined}>
                            <span>{emailContent}</span>
                        </Tooltip>
                        <Tooltip title={t('copy_email', 'Copy Email')}>
                            <Button
                                type="text"
                                size="small"
                                icon={copiedEmailId === record.id ? <CheckOutlined style={{ color: 'green' }} /> : <CopyOutlined />}
                                onClick={() => handleCopy(email, record.id)}
                            />
                        </Tooltip>
                    </Space>
                );
            },
        },
        {
            title: t('contacted'),
            dataIndex: 'contacted',
            key: 'contacted',
            align: 'center',
            filteredValue: filters.contacted !== null && filters.contacted !== undefined ? [String(filters.contacted)] : null,
            filterIcon: (filtered: boolean) => <FilterOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
                <div style={{ padding: 8 }}>
                    <Select
                        allowClear
                        placeholder={t('filter_by_contacted')}
                        value={selectedKeys[0]}
                        style={{ width: '100%', minWidth: 150 }}
                        options={contactedOptions}
                        onChange={(value) => {
                            setSelectedKeys(value ? [value] : []);
                            confirm();
                        }}
                    />
                </div>
            ),
            render: (contacted: boolean, record: DijikolayCustomer) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    {contacted ? (
                        <Tag color="success">{t('contacted_yes')}</Tag>
                    ) : (
                        <Select
                            className='borderless-select'
                            dropdownClassName='rounded-select-dropdown'
                            value={t('contacted_no')}
                            style={{ width: '100%', minWidth: 150 }}
                            onChange={() => onUpdateContacted(record.id)}
                        >
                            <Select.Option key="set-contacted" value={true}>
                                {t('contacted_yes')}
                            </Select.Option>
                        </Select>
                    )}
                </div>
            ),
        },
        {
            title: t('responsible'),
            dataIndex: 'responsible',
            key: 'responsible',
            render: (responsible: string | null) => responsible || '-',
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            filteredValue: filters.status || null,
            filterIcon: (filtered: boolean) => <FilterOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
                <div style={{ padding: 8 }}>
                    <Select
                        autoFocus
                        mode="multiple"
                        allowClear
                        placeholder={t('filter_by_status')}
                        value={selectedKeys}
                        style={{ width: '100%', minWidth: 180 }}
                        options={statusOptions}
                        onChange={(values) => {
                            setSelectedKeys(values);
                            confirm({ closeDropdown: false });
                        }}
                    />
                </div>
            ),
            render: (status: string, record: DijikolayCustomer) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Select
                        className='borderless-select'
                        dropdownClassName='rounded-select-dropdown'
                        value={status}
                        style={{ width: 140 }}
                        onChange={(newStatus) => onUpdateStatus(record.id, newStatus)}
                    >
                        {statusOptions.map(opt => (
                            <Select.Option key={opt.value} value={opt.value}>
                                {opt.label}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            ),
        },
    ];

    return (
        <Row>
            <Col xs={24}>
                <AppTable
                    className="base-table-body"
                    loading={customers.loading}
                    dataSource={filteredCustomers}
                    columns={columns}
                    rowKey="id"
                    paginationConfig={{ pageSize: 10 }}
                    onChange={onChange}
                />
            </Col>
        </Row>
    );
};

export default DijikolayTable;